import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Calendar, User, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      const data = await api.getBlogs(params);
      setBlogs(data);
    } catch (err) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchQuery) {
      fetchBlogs();
      return;
    }
    // Client-side search
    const filtered = blogs.filter(blog =>
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setBlogs(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
      return 'N/A';
    }
  };

  const getImageUrl = (blog, index) => {
    if (blog.image) return blog.image;
    // Fallback images
    const fallbacks = [
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
      'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800',
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
      'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800'
    ];
    return fallbacks[index % fallbacks.length];
  };
  
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section with Image */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1600" 
            alt="Modern workspace" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 to-teal-800/90"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
            data-testid="blog-hero-title"
            data-anim="typing"
            data-speed="35"
          >
            Strategic Insights
          </h1>
          <p
            className="text-xl text-blue-100 max-w-3xl mx-auto mb-8"
            data-anim="fade-up"
            data-delay="120"
          >
            Expert perspectives on <span
              data-anim="rotating-text"
              data-words="talent acquisition|leadership|workforce optimization|hiring trends|industry insights"
              data-speed="3000"
              data-transition="500"
              className="font-semibold text-transparent bg-clip-text text-2xl bg-[linear-gradient(90deg,#fbbf24,#f59e0b,#fcd34d)] bg-[length:200%_100%] animate-[gradient-move_3s_linear_infinite]"
            >talent acquisition</span>, and strategic recruitment.
          </p>
          
          {/* Search - Transparent UI */}
          <div 
            className="max-w-2xl mx-auto"
            data-anim="fade-up"
            data-delay="250"
          >
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={20} />
                <Input
                  placeholder="Search articles..."
                  className="pl-10 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white/30 focus:border-white/50"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="blog-search-input"
                />
              </div>
              <Button 
                onClick={handleSearch}
                className="bg-white text-blue-600 hover:bg-white/90 h-12 px-6"
                data-testid="blog-search-button"
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Loading State */}
      {loading && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-600">Loading blog posts...</p>
          </div>
        </section>
      )}

      {/* No Blogs State */}
      {!loading && blogs.length === 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-600">No blog posts found.</p>
          </div>
        </section>
      )}

      {/* Featured Article */}
      {!loading && blogs.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div 
              className="text-center mb-8"
              data-anim="fade-up"
            >
              <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Featured Article</span>
            </div>
            <Card 
              className="border-none shadow-xl overflow-hidden" 
              data-testid="featured-blog"
              data-anim="scale-in"
              data-delay="100"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <img 
                  src={getImageUrl(blogs[0], 0)}
                  alt={blogs[0].title}
                  className="h-80 lg:h-auto w-full object-cover"
                  loading="lazy"
                />
                <CardContent className="p-12 flex flex-col justify-center">
                  <div className="text-sm font-semibold text-blue-600 mb-3">{blogs[0].category || 'Insights'}</div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{blogs[0].title}</h2>
                  <p className="text-gray-600 mb-6">{blogs[0].excerpt || 'Read more to learn about this topic...'}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    {blogs[0].author && (
                      <div className="flex items-center gap-1">
                        <User size={16} />
                        <span>{blogs[0].author}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{formatDate(blogs[0].created_at)}</span>
                    </div>
                    {blogs[0].readTime && <span>{blogs[0].readTime}</span>}
                  </div>
                  <Link to={`/blog/${blogs[0].id}`}>
                    <Button className="bg-green-600 hover:bg-green-700 text-white w-fit" data-testid="read-featured-blog">
                      Read Article <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>
      )}
      
      {/* Recent Articles */}
      {!loading && blogs.length > 1 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12">
              <h2 
                className="text-4xl font-bold text-gray-900 mb-4"
                data-anim="fade-up"
              >
                Recent Articles
              </h2>
              <p 
                className="text-lg text-gray-600"
                data-anim="fade-up"
                data-delay="100"
              >
                Stay informed with the latest insights from our team of experts
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-anim="list-stagger" data-stagger="100">
              {blogs.slice(1).map((article, index) => (
                <Card key={article.id} className="card border-none shadow-md hover:shadow-xl hover:border-blue-500/50 overflow-hidden transition-all duration-300" data-testid={`blog-card-${index}`}>
                  <img 
                    src={getImageUrl(article, index + 1)}
                    alt={article.title}
                    className="h-48 w-full object-cover"
                    loading="lazy"
                  />
                  <CardContent className="p-6">
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                      {article.category || 'Insights'}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{article.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt || 'Read more...'}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                      {article.author && (
                        <div className="flex items-center gap-1">
                          <User size={14} />
                          <span>{article.author}</span>
                        </div>
                      )}
                      {article.readTime && <span>{article.readTime}</span>}
                    </div>
                    <Link to={`/blog/${article.id}`}>
                      <Button variant="ghost" className="text-blue-600 hover:text-blue-700 p-0">
                        Read More <ArrowRight className="ml-1" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Newsletter CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-bold mb-4"
            data-anim="fade-up"
          >
            Stay Updated
          </h2>
          <p 
            className="text-xl mb-8 text-blue-100"
            data-anim="fade-up"
            data-delay="100"
          >
            Subscribe to our newsletter for the latest insights delivered to your inbox.
          </p>
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
            data-anim="scale-in"
            data-delay="200"
          >
            <Input
              placeholder="Enter your email"
              type="email"
              className="bg-white text-gray-900 h-12"
            />
            <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 h-12 transform hover:scale-105 transition-all">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
